import { planAssetReadErrorMessage } from '@features/fields/lib/planAssetReadError'
import { queryKey as allPlansQueryKey } from '@features/operations/api/useAllPlans'
import {
  type FieldPlansResponse,
  getFieldPlansByIdApi,
  getGuidancePlanApi,
  type Locutus,
  listLineSetsApi,
  locutusModifyPlanApi,
  postGuidancePlanApi,
  postLineSetApi
} from 'api-validations/locutus'
import { useOrgCodeOrThrow } from 'shared-ui/active-operation'
import { useApiClient, useMutation, usePanoramaQuery, useQueryClient } from 'shared-ui/api-provider'
import { useLocalization } from 'shared-ui/localization'
import { useMapContext } from 'shared-ui/map'
import { ApiError } from 'utils'
import type { ApiClient } from 'utils/ApiClient'

/**
 * Guidance the user has built but not yet saved as assets: the line sets the
 * operator can switch between, and the guidance plan that assigns lines to
 * regions.
 */
export type GuidanceDraft = {
  lineSets: Pick<Locutus.LineSetAsset, 'name' | 'data'>[]
  plan: Pick<Locutus.GuidancePlanAsset, 'name' | 'data'>
}

type PlanGuidance = NonNullable<Locutus.Plan['guidance']>

/**
 * Saves each part of a draft as an immutable asset on the field, returning the
 * references a plan's `guidance` holds.
 */
const createGuidanceAssets = async ({
  apiClient,
  orgCode,
  fieldId,
  draft
}: {
  apiClient: ApiClient
  orgCode: string
  fieldId: string
  draft: GuidanceDraft
}): Promise<PlanGuidance> => {
  const [lineSets, plan] = await Promise.all([
    Promise.all(
      draft.lineSets.map((lineSet) =>
        apiClient.callApi(postLineSetApi, { orgCode }, { ...lineSet, fieldId })
      )
    ),
    apiClient.callApi(postGuidancePlanApi, { orgCode }, { ...draft.plan, fieldId })
  ])
  return { lineSets, plan }
}

/**
 * Mutation that saves a draft's assets for a plan that is itself about to be
 * created or updated, resolving to the references to put on it.
 */
export const useCreateGuidanceAssets = (fieldId: string) => {
  const orgCode = useOrgCodeOrThrow()
  const apiClient = useApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (draft: GuidanceDraft) =>
      createGuidanceAssets({ apiClient, orgCode, fieldId, draft }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [orgCode, 'guidance-plans'] })
      queryClient.invalidateQueries({ queryKey: [orgCode, 'line-sets'] })
    }
  })
}

/**
 * A plan's saved guidance, fetched back into the shape the guidance wizard
 * edits. Line sets keep the plan's order; one missing from the field's
 * listing is left out.
 */
export const useSavedGuidance = ({
  fieldId,
  guidance,
  enabled
}: {
  fieldId: string
  guidance: Locutus.Plan['guidance']
  enabled: boolean
}) => {
  const orgCode = useOrgCodeOrThrow()
  const { t } = useLocalization()
  const plan = usePanoramaQuery(
    getGuidancePlanApi,
    { orgCode, id: guidance?.plan.id ?? '' },
    { enabled: enabled && guidance !== undefined }
  )
  const lineSets = usePanoramaQuery(
    listLineSetsApi,
    { orgCode, fieldId },
    { enabled: enabled && guidance !== undefined }
  )

  const data: GuidanceDraft | undefined =
    guidance === undefined || plan.data === undefined || lineSets.data === undefined
      ? undefined
      : {
          plan: plan.data,
          lineSets: guidance.lineSets.flatMap(
            (ref) => lineSets.data.items.find(({ id }) => id === ref.id) ?? []
          )
        }

  const errorMessage =
    plan.error !== null ? planAssetReadErrorMessage(plan.error, t) : lineSets.error?.message

  return { data, isLoading: plan.isLoading || lineSets.isLoading, errorMessage }
}

/**
 * Mutation for giving an existing Plan new guidance. Guidance assets are
 * immutable, so the hook creates them from the draft and then points the Plan
 * at them through `locutusModifyPlanApi`, invalidating plan-list queries on
 * success.
 *
 * The Plan is passed in rather than fetched inside the hook so the caller
 * controls staleness (typically the same component already holds the query
 * data used to pick which plan to edit).
 */
export const useSaveGuidancePlan = (fieldId: string, onSuccess?: () => void) => {
  const orgCode = useOrgCodeOrThrow()
  const apiClient = useApiClient()
  const queryClient = useQueryClient()
  const { map } = useMapContext()

  return useMutation({
    mutationFn: async ({ plan, draft }: { plan: Locutus.Plan; draft: GuidanceDraft }) => {
      try {
        const guidance = await createGuidanceAssets({ apiClient, orgCode, fieldId, draft })

        // Zod-parse the request body to strip server-owned fields (id/orgCode/
        // fieldId/lastModified/lastModifiedBy/createdAt/createdBy).
        const body = locutusModifyPlanApi.requestBodySchema.parse({
          name: plan.name,
          task: plan.task,
          date: plan.date,
          crop: plan.crop,
          customCrop: plan.customCrop,
          boundary: plan.boundary,
          prescriptions: plan.prescriptions,
          tankMixes: plan.tankMixes,
          hybridInputs: plan.hybridInputs,
          guidance
        })

        await apiClient.put(locutusModifyPlanApi.pathFor({ orgCode, fieldId, id: plan.id }), {
          body
        })
        return guidance
      } catch (e) {
        if (e instanceof ApiError) {
          let msg: string | undefined
          try {
            const parsed = JSON.parse(e.responseText) as string | { message?: string }
            msg =
              typeof parsed === 'string'
                ? (JSON.parse(parsed) as { message?: string }).message
                : parsed.message
          } catch {}
          if (msg !== undefined && msg !== '') throw new Error(msg)
          const stripped = e.responseText.replaceAll(`"`, ``)
          throw new Error(stripped || e.message)
        }
        throw e
      }
    },
    onSuccess: (guidance, variables) => {
      // Write the new guidance into the cached plan so consumers of
      // getFieldPlansByIdApi (e.g. the guidance drawer that resolved the
      // targetPlan) see the update immediately, without waiting on the refetch
      // that invalidateQueries triggers. usePanoramaQuery appends apiClient to
      // the key, so we mirror that here to hit the same cache entry.
      const queryKey = [...getFieldPlansByIdApi.queryKey({ orgCode, fieldId }), apiClient]
      queryClient.setQueryData<FieldPlansResponse>(queryKey, (prev) => {
        if (!prev) return prev
        return {
          ...prev,
          plans: prev.plans.map((p) => (p.id === variables.plan.id ? { ...p, guidance } : p))
        }
      })
      // `[orgCode, 'field-plans']` is a prefix that also matches
      // getFieldPlansByIdApi's key (`[orgCode, 'field-plans', { fieldId }]`),
      // so a single invalidation covers both.
      queryClient.invalidateQueries({ queryKey: [orgCode, 'field-plans'] })
      queryClient.invalidateQueries({ queryKey: [allPlansQueryKey, orgCode] })
      queryClient.invalidateQueries({ queryKey: [orgCode, 'guidance-plans'] })
      queryClient.invalidateQueries({ queryKey: [orgCode, 'line-sets'] })

      // Clear the draw tool and hand off to the caller to decide what state
      // to transition into (typically leaving the guidance-drawing pane).
      map.drawTool.clear()
      onSuccess?.()
    }
  })
}
